/**
 * The `Members` tab: every chat member, in server order, with a role label
 * and an `Eliminated` badge for a pool chat's eliminated players.
 *
 * The list is never filtered, sorted or gated on the client
 * (`pool-scope-authorization.mdc`'s chat-scoped correction, plan §5.6). The
 * server already returns only what the viewer may see; a client-side "is
 * this person in my pool" check would be a second, weaker copy of that
 * boundary.
 *
 * Each non-viewer row also gets a trailing `Block` (ISSUE-660 WI-W11), the
 * Members-tab entry point ISSUE-271's plan reserved for whichever card
 * shipped this panel. `member.userId` is passed as `useBlockFan`'s
 * `accountId` the same way `ConversationPane` passes a message's `senderId`:
 * both are chat participant identifiers already in account-id space, per
 * ISSUE-271 plan.
 *
 * The same rows link another member's name to their account profile at
 * `/fan?accountId=` (ISSUE-1027), behind the same known-viewer gate as Block,
 * so the viewer's own name stays plain text.
 */
import {
  useChatListMembers,
  useChatMemberTimeoutClear,
} from "@sportsmet/messaging-sdk/private";
import { Button, Skeleton } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";

import { useAuth } from "../../auth/AuthProvider";
import { openFanProfileLabel } from "../fanProfile/fanProfileCopy";
import { BLOCK_ACTION_LABEL } from "../messageModeration/moderationCopy";
import { useBlockFan } from "../messageModeration/useBlockFan";
import { MemberTimeoutDialog } from "../messages/MemberTimeoutDialog";
import {
  END_TIMEOUT_ACTION_LABEL,
  TIMEOUT_ACTION_LABEL,
  timedOutRosterLabel,
  timeoutActionFor,
} from "../messages/memberTimeoutLogic";
import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import { avatarPlateStyle } from "../profile/avatarPlateStyle";

export type typeMembersTabProps = {
  chatId: string;
  /**
   * ISSUE-1364. The server's `canModerate` from /chat/get. When true, a plain
   * member's row offers Time out, or End timeout while one runs.
   */
  canModerate?: boolean | undefined;
};

function roleLabel(role: "commissioner" | "admin" | "member"): string | null {
  if (role === "commissioner") return "Commissioner";
  if (role === "admin") return "Admin";
  return null;
}

function initial(displayName: string): string {
  return displayName.trim().charAt(0).toUpperCase();
}

export const MembersTab = (props: typeMembersTabProps): ReactNode => {
  const { state: authState } = useAuth();
  // Until the session resolves, `authState.status` is not yet "signedIn" and
  // the viewer's own id is unknown. Rendering Block off an unknown id offers
  // the control on the viewer's own row for that first frame, so the gate
  // below reads this flag rather than comparing against an empty string.
  const isViewerKnown = authState.status === "signedIn";
  const viewerUserId = isViewerKnown ? authState.user.userId : "";
  const { blockFan } = useBlockFan();
  const chatListMembers = useChatListMembers({ chatId: props.chatId });
  const timeoutClear = useChatMemberTimeoutClear();
  const [timeoutTarget, setTimeoutTarget] = useState<{
    userId: string;
    displayName: string;
  } | null>(null);
  const now = new Date();

  const endTimeout = async (userId: string): Promise<void> => {
    try {
      const result = await timeoutClear.mutateAsync({
        chatId: props.chatId,
        userId,
      });
      showApiError(result);
    } catch {
      showOfflineError();
    }
  };

  if (chatListMembers.isLoading) {
    return (
      <div
        data-testid="chat-details-members-loading"
        className="flex flex-col gap-3"
      >
        <Skeleton shape="text" lines={3} label="Loading chat members" />
      </div>
    );
  }

  const members = chatListMembers.data?.data?.items ?? [];

  if (members.length === 0) {
    return (
      <div
        data-testid="chat-details-members-empty"
        className="flex flex-col items-center gap-1 py-10 text-center"
      >
        <p className="text-body-strong text-ink">Nobody has joined yet.</p>
      </div>
    );
  }

  return (
    <>
      <ul data-testid="chat-details-members" className="flex flex-col gap-3">
        {members.map((member) => {
          const label = roleLabel(member.role);
          const timeoutAction = isViewerKnown
            ? timeoutActionFor({
                viewerUserId,
                canModerate: props.canModerate === true,
                targetUserId: member.userId,
                targetRole: member.role,
                targetTimedOutUntilIso: member.timedOutUntilIso,
                now,
              })
            : null;
          return (
            <li
              key={member.userId}
              data-testid="member-row"
              className="flex items-center gap-3"
            >
              {member.avatarUrl ? (
                <img
                  src={member.avatarUrl}
                  alt=""
                  className="size-10 shrink-0 rounded-full object-cover"
                />
              ) : (
                <span
                  aria-hidden="true"
                  className="grid size-10 shrink-0 place-items-center rounded-full text-body-strong"
                  style={avatarPlateStyle(member.displayName)}
                >
                  {initial(member.displayName)}
                </span>
              )}
              {isViewerKnown && member.userId !== viewerUserId ? (
                <Link
                  to="/fan"
                  search={{ accountId: member.userId }}
                  data-testid={`member-profile-link-${member.userId}`}
                  aria-label={openFanProfileLabel(member.displayName)}
                  className="flex min-h-touch-target flex-1 items-center text-body text-ink underline-offset-2 hover:underline"
                >
                  {member.displayName}
                </Link>
              ) : (
                <span className="flex-1 text-body text-ink">
                  {member.displayName}
                </span>
              )}
              {label !== null ? (
                <span className="text-caption text-ink-secondary">{label}</span>
              ) : null}
              {timeoutAction === "endTimeout" && member.timedOutUntilIso ? (
                <span
                  data-testid={`member-timed-out-${member.userId}`}
                  className="text-caption text-ink-secondary"
                >
                  {timedOutRosterLabel(member.timedOutUntilIso, now)}
                </span>
              ) : null}
              {timeoutAction !== null ? (
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid={`member-${timeoutAction === "timeout" ? "timeout" : "end-timeout"}-${member.userId}`}
                  loading={
                    timeoutClear.isPending &&
                    timeoutClear.variables.userId === member.userId
                  }
                  onClick={() => {
                    if (timeoutAction === "timeout") {
                      setTimeoutTarget({
                        userId: member.userId,
                        displayName: member.displayName,
                      });
                    } else {
                      void endTimeout(member.userId);
                    }
                  }}
                >
                  {timeoutAction === "timeout"
                    ? TIMEOUT_ACTION_LABEL
                    : END_TIMEOUT_ACTION_LABEL}
                </Button>
              ) : null}
              {member.isEliminated === true ? (
                <span
                  data-testid="member-eliminated-badge"
                  className="rounded-full bg-surface-secondary px-2 py-1 text-caption text-ink-secondary"
                >
                  Eliminated
                </span>
              ) : null}
              {isViewerKnown && member.userId !== viewerUserId ? (
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid={`member-block-${member.userId}`}
                  onClick={() => {
                    void blockFan({
                      accountId: member.userId,
                      displayName: member.displayName,
                    });
                  }}
                >
                  {BLOCK_ACTION_LABEL}
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
      <MemberTimeoutDialog
        chatId={props.chatId}
        target={timeoutTarget}
        onClose={() => {
          setTimeoutTarget(null);
        }}
      />
    </>
  );
};
